// @ts-check

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {number} threshold
 * @property {Element|null} [root]
 * @property {string} [rootMargin]
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */

class ViewportObserver {
  /**
   * @param {Object} options
   * @param {IntersectionObserverOptions} options.intersectionObserverOptions
   * @param {(element: Element, extra: any) => void} options.callback
   */
  constructor(options) {
    this.callback = options.callback
    // Bind event handlers to class instance
    this.onElementVisible = this.onElementVisible.bind(this)
    this.onIntersectionChange = this.onIntersectionChange.bind(this)
    this.onPageFullyLoaded = this.onPageFullyLoaded.bind(this)
    // Initialize instance state
    /** @type {WeakMap<Element, any>} */
    this.elements = new WeakMap()
    /** @type {Set<Element>} */
    this.deferVisible = new Set()
    // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
    this.observer = new IntersectionObserver(
      this.onIntersectionChange,
      options.intersectionObserverOptions
    )
    this.isDeferred = document.readyState !== 'complete'
    // Wait for page load completion before marking elements as visible
    if (this.isDeferred) {
      document.addEventListener('DOMContentLoaded', this.onPageFullyLoaded)
    }
  }

  // API

  /**
   * @param {Element} element
   * @param {any} extra
   */
  observe(element, extra) {
    this.elements.set(element, extra)
    this.observer.observe(element)
  }

  /**
   * @param {Element} element
   */
  unobserve(element) {
    this.observer.unobserve(element)
    this.elements.delete(element)
    this.deferVisible.delete(element)
  }

  // Event handlers

  onPageFullyLoaded() {
    // Flush the deferred visible elements
    this.deferVisible.forEach(this.onElementVisible)
    this.deferVisible.clear()
    // Stop listening for page load
    this.isDeferred = false
    document.removeEventListener('DOMContentLoaded', this.onPageFullyLoaded)
  }

  /**
   * @param {Array.<IntersectionObserverEntry>} entries
   */
  onIntersectionChange(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (this.isDeferred) {
          this.deferVisible.add(entry.target)
        } else {
          this.onElementVisible(entry.target)
        }
      }
    })
  }

  /**
   * @param {Element} element
   */
  onElementVisible(element) {
    if (this.elements.has(element)) {
      const extra = this.elements.get(element)
      this.callback(element, extra)
    }
    this.unobserve(element)
  }
}

module.exports = ViewportObserver
